import { Guest } from 'shared-types/Basket';
import { Button } from '@common/components/Button';
import { TextArea } from '@common/components/FormElements/Textarea';
import Portal from '@common/components/Portal';
import { StickyBarContent } from '@common/components/StickyBar';
import { useTranslations } from '@lang';
import { isGuestAlreadyInArray } from '@utils/helpers';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ButtonWrapper } from '../../components/ExperienceDetails/ExperienceDetails.styles';
import { GuestDetails } from '../../components/GuestForm/GuestDetails';
import { GuestFieldFormSchema, MappedGuest } from '../../types';
import { usePackageConfiguration, useWizardNavigation, useCurrentStep } from '../../contexts';
import {
  FormStepSectionWrapper,
  NotesWrapper,
  PageTitle,
  StyledDateRangeIndicator,
} from '../FormSteps.styles';
import { useHashNavigation } from '../hooks/useHashNavigation';
import { GuestDetailsWrapper, TitleWrapper } from './GuestDetailsStep.styles';
import { mapLoggedInUserToGuest } from '../../helpers/mapLoggedInUserToGuest';
import { useAddExistingGuest } from '../hooks/useAddExistingGuest';
import { AddExistingGuests } from '../../components/AddExistingGuests';

export const GuestDetailsStep = () => {
  const { t } = useTranslations();

  const { journeyParams, rate, room, basketGuests, loggedInGuest } = usePackageConfiguration();
  const { stickyBarId, handleNextStep } = useWizardNavigation();
  const { packageTotalPrice, hash } = useCurrentStep();

  useHashNavigation({ hash });
  const mappedloggedInGuestData = mapLoggedInUserToGuest(loggedInGuest);
  const [availableGuests, setAvailableGuests] = useState<MappedGuest[]>(
    mappedloggedInGuestData ? [mappedloggedInGuestData] : []
  );

  const { handleGuestPillClick, selectedGuests, existingGuests } = useAddExistingGuest({
    availableGuests,
    basketGuests,
  });

  const { control, watch } = useFormContext();
  const guestsByRoom = watch('guests');
  const guests = (guestsByRoom?.rooms || []).flat();
  const isValid = GuestFieldFormSchema.safeParse(guestsByRoom).success;
  const updateAvailableGuestsOnSubmit = () => {
    const updatedAvailableGuests = availableGuests;
    guests.forEach((guest) => {
      if (
        guest.firstName &&
        guest.lastName &&
        !isGuestAlreadyInArray(updatedAvailableGuests, guest)
      ) {
        updatedAvailableGuests.push(guest);
      }
    });
    setAvailableGuests(updatedAvailableGuests);
  };
  const { adults, children } = journeyParams;
  const onSubmit = () => {
    updateAvailableGuestsOnSubmit();
    handleNextStep();
  };

  const metaEntity = rate;
  const selectedEntity = room;

  return selectedEntity ? (
    <GuestDetailsWrapper>
      <TitleWrapper>
        <PageTitle noBorder>{t('common.guests')}</PageTitle>
      </TitleWrapper>

      <FormStepSectionWrapper isLoggedIn={true}>
        {/* hardcoded for now until we get designs for the signin modal */}
        <AddExistingGuests
          selectedGuests={selectedGuests}
          existingGuests={existingGuests as Guest[]}
          isGenderSpecific={false}
          guest={loggedInGuest as MappedGuest}
          handleGuestPillClick={handleGuestPillClick}
        />
        <GuestDetails
          isGenderSpecific={false}
          numberOfGuests={adults}
          isDisabled={false}
          withGenderSelect={false}
        />
        {children ? <>+{t('basket.children', { children })}</> : null}
        <NotesWrapper>
          <Controller
            name={'guests.notes'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextArea
                isFullWidth
                labelWidth="150px"
                name="note"
                label={t('rooms.notes')}
                id="additional-notes"
                onChange={(val) => {
                  if (val) {
                    onChange(val);
                  }
                }}
                input={{
                  value,
                }}
                error={''}
              />
            )}
          />
        </NotesWrapper>
      </FormStepSectionWrapper>
      <Portal selector={`#${stickyBarId}`}>
        <StickyBarContent
          name={metaEntity.title || ''}
          subtitle={
            <StyledDateRangeIndicator
              fromDate={journeyParams.fromDate}
              toDate={journeyParams.toDate}
            />
          }
          displayedPrice={packageTotalPrice}
          button={
            <ButtonWrapper>
              <Button isPrimary onClick={onSubmit} disabled={!isValid}>
                {t('spa.package.nextStep')}
              </Button>
            </ButtonWrapper>
          }
        />
      </Portal>
    </GuestDetailsWrapper>
  ) : null;
};
